import type { Lang } from './text.js';

/**
 * Points, the leaderboard and the evening wrapped up, in plain words.
 *
 * Same rules as the rest of the guest app: short, everyday words, many guests reading in their
 * second language. The cards made for sharing name no dates, no times and no brands, and they
 * call the eras by name rather than by year.
 */

type Era = 1 | 2 | 3 | 4 | 5;

export interface ScoreCopy {
  points: (n: number) => string;
  /** Under WELL DONE: how the game went against its target time. */
  runLine: (ms: number, parMs: number, mistakes: number) => string;
  eraName: Record<Era, string>;
  ordinal: (rank: number) => string;
  /** The leaderboard card at the end of an era. */
  boardTitle: string;
  boardHow: string;
  you: string;
  boardYou: (rank: number, of: number, points: number, shared: boolean) => string;
  /** `final` at docking: then it is the whole evening, not "so far". */
  boardVoyage: (rank: number, points: number, final: boolean) => string;
  /** For a guest with nothing this era: no rank read out, just the next chance (if there is one). */
  boardNone: (final: boolean) => string;
  boardEmpty: string;
  hide: string;
  /** The toolbar chip that brings the leaderboard back. */
  scores: string;
  /** Wrapped: the entry, the viewer, and the cards. */
  wrappedOpen: string;
  wrappedOpenSub: string;
  wrappedMaking: string;
  wrappedSave: string;
  wrappedSaveAll: string;
  wrappedSaved: string;
  wrappedFailed: string;
  wrappedClose: string;
  wrappedTap: string;
  cardYouKicker: string;
  cardYouRank: (rank: number, of: number, shared: boolean) => string;
  cardYouNone: string;
  cardFastKicker: string;
  cardFastPace: (pace: number) => string;
  cardFastPar: (era: Era, parMs: number) => string;
  cardBestKicker: string;
  cardBestLine: (points: number, rank: number, shared: boolean) => string;
  cardShipKicker: string;
  /** The words under each count; the number is drawn on its own, big. */
  cardFixes: (n: number) => string;
  cardBallots: (n: number) => string;
  cardGames: (n: number) => string;
  cardResultKicker: string;
  /** How the voyage ended: no winner, only the damage the ship came home with (2026-09-29). */
  cameHome: (damage: number) => string;
  cardUprising: string;
  cardRoleKicker: string;
  roleCrew: string;
  roleSaboteur: string;
  roleCrewLine: string;
  roleSaboteurLine: string;
  /** The eras this guest was a saboteur in, by name. */
  roleSaboteurIn: (eras: readonly string[]) => string;
  footer: string;
  seconds: (ms: number) => string;
}

/** 1 240, with a space that never breaks. */
export function fmt(n: number): string {
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
}

/** 1ST, 2ND, 3RD, 4TH … 11TH, 12TH. */
function englishOrdinal(n: number): string {
  const tens = n % 100;
  const suffix = tens >= 11 && tens <= 13 ? 'TH' : (['TH', 'ST', 'ND', 'RD'][n % 10] ?? 'TH');
  return `${n}${suffix}`;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

const en: ScoreCopy = {
  points: (n) => `${fmt(n)} POINTS`,
  runLine: (ms, par, mistakes) =>
    `${(ms / 1000).toFixed(1)} s · target ${(par / 1000).toFixed(1)} s · ${mistakes === 1 ? '1 mistake' : `${mistakes} mistakes`}`,
  eraName: { 1: 'EARTH', 2: 'THE BOOM', 3: 'THE WAR', 4: 'UNDERGROUND', 5: 'BACK ON THE SURFACE' },
  ordinal: englishOrdinal,
  boardTitle: 'SCORES · THIS ERA',
  boardHow: 'Faster, with fewer mistakes = more points.',
  you: 'YOU',
  boardYou: (rank, of, points, shared) =>
    `You: ${shared ? 'joint ' : ''}${englishOrdinal(rank).toLowerCase()} of ${of} · ${fmt(points)} points`,
  boardVoyage: (rank, points, final) =>
    `${final ? 'Whole evening' : 'The evening so far'}: ${englishOrdinal(rank).toLowerCase()} · ${fmt(points)} points`,
  boardNone: (final) => (final ? 'No points for you this era.' : 'No points for you this era. The next one is yours!'),
  boardEmpty: 'Nobody has finished a game yet this era.',
  hide: 'HIDE',
  scores: 'SCORES',
  wrappedOpen: 'YOUR EVENING, WRAPPED',
  wrappedOpenSub: 'your night as story cards · save and share',
  wrappedMaking: 'MAKING YOUR CARDS…',
  wrappedSave: 'SAVE OR SHARE',
  wrappedSaveAll: 'SAVE ALL',
  wrappedSaved: '✓ SAVED',
  wrappedFailed: 'This phone would not save it. A screenshot works too.',
  wrappedClose: 'CLOSE',
  wrappedTap: 'tap the sides or swipe for the next card',
  cardYouKicker: 'YOUR EVENING, WRAPPED',
  cardYouRank: (rank, of, shared) => `${shared ? 'JOINT ' : ''}${englishOrdinal(rank)} OF ${of} ON BOARD`,
  cardYouNone: 'ON BOARD FOR THE WHOLE VOYAGE',
  cardFastKicker: 'YOUR FASTEST GAME',
  cardFastPace: (pace) =>
    pace >= 1.05 ? `${pace.toFixed(1)}× FASTER THAN TARGET` : pace >= 0.95 ? 'RIGHT ON TARGET' : 'SLOW AND STEADY',
  cardFastPar: (era, par) => `${en.eraName[era]} · TARGET ${(par / 1000).toFixed(1)} S`,
  cardBestKicker: 'YOUR BEST ERA',
  cardBestLine: (points, rank, shared) =>
    `${fmt(points)} POINTS · ${shared ? 'JOINT ' : ''}${englishOrdinal(rank)} AT THE TABLE`,
  cardShipKicker: 'WHAT YOU DID FOR THE SHIP',
  cardFixes: (n) => `${plural(n, 'ALARM', 'ALARMS')} YOU HELPED FIX`,
  cardBallots: (n) => `${plural(n, 'VOTE', 'VOTES')} YOU CAST`,
  cardGames: (n) => `${plural(n, 'GAME', 'GAMES')} FINISHED`,
  cardResultKicker: 'HOW IT ENDED',
  cameHome: (damage) => (damage === 0 ? 'THE SHIP CAME HOME WITHOUT A SCRATCH' : `THE SHIP CAME HOME WITH ${damage} DAMAGE`),
  cardUprising: 'THE SABOTEURS TOOK THE BOOM AND SET THE MACHINES LOOSE',
  cardRoleKicker: 'YOUR ROLE TONIGHT',
  roleCrew: 'CREW',
  roleSaboteur: 'SABOTEUR',
  roleCrewLine: 'You kept the ship going, and kept your eyes open.',
  roleSaboteurLine: 'You were a secret saboteur. Now everyone knows.',
  roleSaboteurIn: (eras) =>
    `A secret saboteur in ${eras.length > 1 ? `${eras.slice(0, -1).join(', ')} and ${eras[eras.length - 1]}` : (eras[0] ?? '')}. Now everyone knows.`,
  footer: 'SS AURORA · 765 YEARS · ONE EVENING',
  seconds: (ms) => `${(ms / 1000).toFixed(1)} S`,
};

const no: ScoreCopy = {
  points: (n) => `${fmt(n)} POENG`,
  runLine: (ms, par, mistakes) =>
    `${(ms / 1000).toFixed(1).replace('.', ',')} s · mål ${(par / 1000).toFixed(1).replace('.', ',')} s · ${mistakes === 1 ? '1 feil' : `${mistakes} feil`}`,
  eraName: { 1: 'JORDA', 2: 'BOOMEN', 3: 'KRIGEN', 4: 'UNDER SANDEN', 5: 'TILBAKE PÅ OVERFLATEN' },
  ordinal: (rank) => `NR. ${rank}`,
  boardTitle: 'POENG · DENNE TIDSALDEREN',
  boardHow: 'Raskere, med færre feil = flere poeng.',
  you: 'DEG',
  boardYou: (rank, of, points, shared) => `Du: ${shared ? 'delt ' : ''}nr. ${rank} av ${of} · ${fmt(points)} poeng`,
  boardVoyage: (rank, points, final) => `${final ? 'Hele kvelden' : 'Kvelden så langt'}: nr. ${rank} · ${fmt(points)} poeng`,
  boardNone: (final) =>
    final ? 'Ingen poeng til deg denne tidsalderen.' : 'Ingen poeng til deg denne tidsalderen. Den neste er din!',
  boardEmpty: 'Ingen har fullført et spill ennå i denne tidsalderen.',
  hide: 'SKJUL',
  scores: 'POENG',
  wrappedOpen: 'KVELDEN DIN, OPPSUMMERT',
  wrappedOpenSub: 'kvelden din som story-kort · lagre og del',
  wrappedMaking: 'LAGER KORTENE DINE…',
  wrappedSave: 'LAGRE ELLER DEL',
  wrappedSaveAll: 'LAGRE ALLE',
  wrappedSaved: '✓ LAGRET',
  wrappedFailed: 'Telefonen ville ikke lagre det. Et skjermbilde fungerer også.',
  wrappedClose: 'LUKK',
  wrappedTap: 'trykk på sidene eller sveip for neste kort',
  cardYouKicker: 'KVELDEN DIN, OPPSUMMERT',
  cardYouRank: (rank, of, shared) => `${shared ? 'DELT ' : ''}NR. ${rank} AV ${of} OM BORD`,
  cardYouNone: 'OM BORD HELE REISEN',
  cardFastKicker: 'DITT RASKESTE SPILL',
  cardFastPace: (pace) =>
    pace >= 1.05
      ? `${pace.toFixed(1).replace('.', ',')} GANGER RASKERE ENN MÅLET`
      : pace >= 0.95
        ? 'AKKURAT PÅ MÅLET'
        : 'SAKTE, MEN SIKKERT',
  cardFastPar: (era, par) => `${no.eraName[era]} · MÅL ${(par / 1000).toFixed(1).replace('.', ',')} S`,
  cardBestKicker: 'DIN BESTE TIDSALDER',
  cardBestLine: (points, rank, shared) => `${fmt(points)} POENG · ${shared ? 'DELT ' : ''}NR. ${rank} VED BORDET`,
  cardShipKicker: 'DETTE GJORDE DU FOR SKIPET',
  cardFixes: (n) => `${plural(n, 'ALARM', 'ALARMER')} DU HJALP Å FIKSE`,
  cardBallots: (n) => `${plural(n, 'STEMME', 'STEMMER')} DU AVGA`,
  cardGames: (n) => `${plural(n, 'SPILL', 'SPILL')} FULLFØRT`,
  cardResultKicker: 'SLIK ENDTE DET',
  cameHome: (damage) =>
    damage === 0 ? 'SKIPET KOM HJEM UTEN EN SKRAMME' : `SKIPET KOM HJEM MED ${damage} ${damage === 1 ? 'SKADE' : 'SKADER'}`,
  cardUprising: 'SABOTØRENE TOK BOOMEN OG SLAPP MASKINENE LØS',
  cardRoleKicker: 'DIN ROLLE I KVELD',
  roleCrew: 'MANNSKAP',
  roleSaboteur: 'SABOTØR',
  roleCrewLine: 'Du holdt skipet i gang, og holdt øynene åpne.',
  roleSaboteurLine: 'Du var en hemmelig sabotør. Nå vet alle det.',
  roleSaboteurIn: (eras) =>
    `Hemmelig sabotør i ${eras.length > 1 ? `${eras.slice(0, -1).join(', ')} og ${eras[eras.length - 1]}` : (eras[0] ?? '')}. Nå vet alle det.`,
  footer: 'SS AURORA · 765 ÅR · ÉN KVELD',
  seconds: (ms) => `${(ms / 1000).toFixed(1).replace('.', ',')} S`,
};

export const SCORE_COPY: Record<Lang, ScoreCopy> = { en, no };
